/**
 * 사람(app_user). 비밀번호는 전부 `MOCK_PASSWORD` 다.
 *
 * 고정 인사 정보는 생년월일뿐이다. 소속·직급 같은 칸은 사무소가 정의한 직원 항목(staffFields)이고
 * 값은 사람마다 `fields` 에 {항목 id: 값}으로 들어 있다.
 *
 * 역할은 관리자·직원 둘뿐이고 관리자는 한 명이다. HR 과 PM 은 사람에게 다는 표시(is_hr, is_pm)이고,
 * PM 은 권한이 없는 이름표다. 재무(프로젝트 금액, 개인 임금)는 관리자만 본다.
 */
import type { PayType, Salary, Staff, StaffField } from "@/types";

export const MOCK_PASSWORD = "password";

/** 사무소가 정의한 직원 항목 — 직원 상세의 기본 정보에서 「항목 추가」로 만든다. */
export const staffFields: StaffField[] = [
  {
    id: "sf-dept",
    name: "소속",
    value_type: "select",
    options: ["설계", "감리", "경영지원"],
    is_public: true,
    is_seed: true,
  },
  {
    id: "sf-title",
    name: "직급",
    value_type: "text",
    options: [],
    is_public: true,
    is_seed: true,
  },
  // 전화번호는 모두가 보는 기본 정보다.
  {
    id: "sf-phone",
    name: "전화번호",
    value_type: "text",
    options: [],
    is_public: true,
    is_seed: false,
  },
];

const person = (
  id: string,
  name: string,
  email: string,
  profile: Partial<Staff> & Pick<Staff, "hired_on">
): Staff => ({
  id,
  name,
  email,
  role: "staff",
  status: "active",
  birth_date: null,
  resigned_on: null,
  // 전원이 사무소 공통값을 쓴다 — Daily 시드가 하루 8시간으로 쌓여 있어 사람마다 다르게 두면
  // 정규·연장이 시드와 어긋난다. 개인별 기준은 직원 상세에서 켠다.
  standard_daily_hours: null,
  standard_monthly_hours: null,
  is_hr: false,
  has_password: true,
  created_via_google: false,
  totp_enabled: false,
  avatar_url: null,
  fields: {},
  ...profile,
});

export const staff: Staff[] = [
  person("u-owner", "김태호", "owner@workthings.test", {
    role: "admin",
    totp_enabled: true,
    hired_on: "2012-03-02",
    birth_date: "1975-04-12",
    fields: { "sf-dept": "경영지원", "sf-title": "대표", "sf-phone": "010-1234-0001" },
  }),
  person("변경민", "변경민", "chgemn@gmail.com", {
    role: "staff",
    is_hr: true,
    hired_on: "2020-02-03",
  }),
  person("u-pm", "노은진", "pm@workthings.test", {
    role: "staff",
    hired_on: "2016-01-04",
    birth_date: "1984-09-30",
    fields: { "sf-dept": "설계", "sf-title": "실장" },
  }),
  person("u-pm2", "송지하", "song@workthings.test", {
    role: "staff",
    hired_on: "2017-05-08",
  }),
  // 10월 12일부터 육아휴직 — 그 뒤로는 Daily 가 없다(LAST_WORKDAY).
  person("u-pm3", "박준우", "park@workthings.test", {
    role: "staff",
    status: "leave",
    hired_on: "2018-02-19",
  }),
  person("u-staff", "이도윤", "staff@workthings.test", {
    hired_on: "2024-07-01",
    birth_date: "1998-02-14",
    fields: { "sf-dept": "설계", "sf-title": "사원" },
  }),
  person("u-st2", "김서연", "kim.y@workthings.test", { hired_on: "2021-03-02" }),
  person("u-st3", "오세진", "oh@workthings.test", { hired_on: "2025-01-06" }),
  person("u-st4", "최다민", "choi@workthings.test", { hired_on: "2022-08-01" }),
  person("u-st5", "한예솔", "han@workthings.test", {
    hired_on: "2026-07-01",
    has_password: false,
    created_via_google: true,
  }),
  // 9월 30일 퇴사 — 계정은 남아 있고 그 뒤로는 Daily 가 없다(LAST_WORKDAY).
  person("u-st6", "윤지아", "yoon@workthings.test", {
    status: "resigned",
    hired_on: "2023-04-03",
    resigned_on: "2026-09-30",
  }),
  person("u-acc", "정유리", "accounting@workthings.test", {
    hired_on: "2019-09-16",
  }),
];

/** Daily 를 쓴 마지막 날 — 휴직, 퇴사한 사람. */
export const LAST_WORKDAY: Record<string, string> = {
  "u-pm3": "2026-10-09",
  "u-st6": "2026-09-30",
};

/**
 * 기간별 급여. 덮어쓰지 않고 쌓는다 — 올린 사람은 이전 기간이 닫힌 채로 남는다.
 * 프리랜서는 인건비 원가 산정에서 빠진다.
 */
const salary = (
  userId: string,
  payType: PayType,
  amount: number,
  from: string,
  to: string | null,
  overhead = 0.3
): Salary & { user_id: string } => ({
  id: `sal-${userId}-${from}`,
  user_id: userId,
  pay_type: payType,
  amount,
  overhead_rate: payType === "freelancer" ? 0 : overhead,
  effective_from: from,
  effective_to: to,
  created_at: `${from}T09:00:00+09:00`,
  hourly_cost: hourlyCost(payType, amount, payType === "freelancer" ? 0 : overhead),
});

/** 환산 시간단가 — 서버와 같은 셈. 월 기준 근무시간은 회사 설정의 174시간으로 둔다. */
export const hourlyCost = (payType: PayType, amount: number, overhead: number) =>
  payType === "freelancer"
    ? null
    : Math.round((payType === "monthly" ? amount / 174 : amount) * (1 + overhead));

export const salaries: (Salary & { user_id: string })[] = [
  salary("u-owner", "monthly", 9000000, "2024-01-01", null),
  salary("변경민", "monthly", 5200000, "2024-01-01", "2026-01-01"),
  salary("변경민", "monthly", 5600000, "2026-01-01", null),
  salary("u-pm", "monthly", 6100000, "2025-01-01", null),
  salary("u-pm2", "monthly", 5800000, "2025-01-01", null),
  salary("u-pm3", "monthly", 5500000, "2025-01-01", null),
  salary("u-staff", "monthly", 3400000, "2024-07-01", null),
  salary("u-st2", "monthly", 4300000, "2025-01-01", null),
  salary("u-st3", "hourly", 22000, "2025-01-06", null),
  salary("u-st4", "monthly", 4600000, "2025-01-01", null),
  salary("u-st5", "freelancer", 2600000, "2026-07-01", null),
  salary("u-st6", "monthly", 3900000, "2024-01-01", "2026-10-01"),
  salary("u-acc", "monthly", 4800000, "2025-01-01", null),
];
